import React from 'react';
import {ScrollView, TouchableOpacity, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useSelector} from 'react-redux';

import useWindowDimensionsSafe from 'src/Modules/Native/useWindowDimensionsSafe';

////////////// utils
import {formatNumberDisplayAmount} from 'src/Utils/formatHelper';
import {markClassicLocationAllComplete} from 'src/Utils/huntFunctions_classic';

import {apiCheckAnyChallenge} from '../../Utils/apiCalls';

/////////////// components
import {navigationRef} from 'src/Nav/navigationHelpers';
import {
  BodyText,
  ClassicChallengeCard,
  ClassicIconButton,
  CloseIconButton,
} from 'src/Modules/CommonView';
import {useLocation} from '../../Hooks/gameHooks';
import {useUserId} from '../../Hooks/reduxHooks';
import CustomActivityIndicator from '../../Modules/CommonView/CustomActivityIndicator';
import {classicPhotoVideoCompletionEvent} from '../../Utils/huntFunctions_classic';

const LocationChallengeListModal = () => {
  const {width, height} = useWindowDimensionsSafe();

  const userId = useUserId();
  const groupId = useSelector((state) => state.groupInfo?.groupId);
  const eventId = useSelector((state) => state.event?.eventId);
  const locations = useSelector((state) => state.game_v2);
  const allChallenges = useSelector((state) => state.game_v2.allChallenges);
  const location = useLocation();
  const {locationId, name, challengeList} = location;

  const checkMarkAllLocationChalsComplete = (challenge) => {
    const {locationId} = location;

    const allChallengesComplete = challengeList.every((key) => {
      let locaChallenge = allChallenges[key];
      return !!locaChallenge?.completeTime || key == challenge.challengeId;
    });

    if (allChallengesComplete) {
      markClassicLocationAllComplete({groupId, locationId});
    }
  };

  const handlePhotoComplete = async ({photoUrl, shareUUID, challenge}) => {
    const {challengeId, question} = challenge;

    await apiCheckAnyChallenge({
      group_id: groupId,
      user_id: userId,
      photo_url: photoUrl,
      share_UUID: shareUUID,
    });

    classicPhotoVideoCompletionEvent({
      photoUrl: photoUrl,
      challengeId,
      challenge,
      groupId,
      shareUUID,
      description: question,
      eventId,
    });
  };

  const handleVideoComplete = ({photoUrl, thumbnail, shareUUID, challenge}) => {
    const {challengeId, question} = challenge;

    classicPhotoVideoCompletionEvent({
      photoUrl: photoUrl,
      thumbnail: thumbnail,
      challengeId,
      userId,
      groupId,
      shareUUID,
      description: question,
      eventId,
    });
  };

  const handleSelectChallenge = (challenge) => {
    const {type, completeTime} = challenge;

    if (type === 'photo' || type === 'video') {
      return navigationRef.navigate('PhotoVideoCamera', {
        onCapture: (data) => {
          if (type === 'photo') {
            handlePhotoComplete({
              photoUrl: data?.downloadURL,
              shareUUID: data?.shareUUID,
              challenge,
            });
            checkMarkAllLocationChalsComplete(challenge);
          } else if (type === 'video') {
            handleVideoComplete({
              thumbnail: data?.thumbnail,
              photoUrl: data?.downloadURL,
              shareUUID: data?.shareUUID,
              challenge,
            });

            checkMarkAllLocationChalsComplete(challenge);
          }
        },
        onReport: () => {
          navigationRef.navigate('ReportModal');
        },
        text: challenge.question,
        shareUUID: challenge.shareUUID,
        photoUrl: challenge.photoUrl,
        isVideo: type == 'video',
      });
    }

    if (!!completeTime) {
      return navigationRef.navigate('ChallengeResultModal', {
        challengeId: challenge.challengeId,
      });
    }
    return navigationRef.navigate('ChallengeAttemptModal', {
      challengeId: challenge.challengeId,
    });
  };

  if (!locations || !allChallenges) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: global?.color?.white,
        }}>
        <CustomActivityIndicator />
      </View>
    );
  }

  var totalLocationPoints = 0;
  (challengeList || [])?.map?.((key) => {
    let challenge = allChallenges[key];
    let score = challenge.points || 100;
    totalLocationPoints = score + totalLocationPoints;
  }, 0);

  let completedChallenges = 0;
  let earnedLocationPoints = 0;
  (challengeList || [])?.map?.((key) => {
    let challenge = allChallenges[key];
    let score = challenge.groupPointsEarned || 0;
    if (challenge?.completeTime) {
      completedChallenges++;
    }
    earnedLocationPoints = score + earnedLocationPoints;
  }, 0);

  return (
    <SafeAreaView style={{flex: 1, justifyContent: 'flex-end'}}>
      <TouchableOpacity
        style={{flex: 1}}
        onPress={() => navigationRef.goBack(null)}
      />
      <View
        style={{
          backgroundColor: global?.color?.white,
          width,
          marginTop: 5,
          paddingVertical: 5,
          borderRadius: 10,
          position: 'relative',
        }}>
        <View style={{marginTop: 10}}>
          <BodyText
            text={name}
            textStyle={{
              fontWeight: '700',
              textAlign: 'center',
              marginHorizontal: 30,
            }}
          />
          <View
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap',
              borderBottomWidth: 1,
              borderBottomColor: global?.color?.white1,
              padding: 10,
              paddingVertical: 20,
            }}>
            <View style={{marginHorizontal: 5}}>
              <ClassicIconButton
                handlePress={() => {
                  navigationRef.goBack(null);
                  navigationRef.navigate('NavigationChallengeModal', {
                    locationId,
                  });
                }}
                size={24}
                text="Map"
                icon="map-o"
                color="blue"
                inline
              />
            </View>
            <View style={{marginHorizontal: 5}}>
              <ClassicIconButton
                text={`${
                  earnedLocationPoints
                    ? `${formatNumberDisplayAmount(
                        earnedLocationPoints,
                      )}/${formatNumberDisplayAmount(totalLocationPoints)}`
                    : formatNumberDisplayAmount(totalLocationPoints)
                } pts`}
                size={24}
                icon="flash"
                color="teal"
                inline
              />
            </View>
            <View style={{marginHorizontal: 5}}>
              <ClassicIconButton
                text={`${completedChallenges}/${challengeList?.length} Challenges`}
                size={24}
                icon="compass"
                color="orange"
                inline
              />
            </View>
          </View>
        </View>
        <CloseIconButton
          handlePress={() => navigationRef.goBack(null)}
          side="right"
        />
        <ScrollView style={{maxHeight: height - 360}}>
          {!!challengeList &&
            !!challengeList?.length &&
            challengeList?.map?.((challengeId) => {
              let challenge = allChallenges[challengeId];
              const {
                type,
                question,
                points = 100,
                groupPointsEarned = 0,
                completeTime,
              } = challenge;
              console.log('LocationChallengeListModal --', {
                challengeList,
                challenge,
              });

              return (
                <ClassicChallengeCard
                  key={`c_l_item_${challengeId}${challenge.index}`}
                  text={question}
                  icon={
                    type === 'photo'
                      ? 'camera'
                      : type === 'video'
                      ? 'video-camera'
                      : type === 'location'
                      ? 'map-marker'
                      : type === 'numberguesser' || type === 'number_guesser'
                      ? 'sliders'
                      : type === 'multiple_choice'
                      ? 'university'
                      : type === 'true_false'
                      ? 'question-circle'
                      : type === 'fill_in_the_blank'
                      ? 'braille'
                      : 'university'
                  }
                  backgroundColor={
                    type === 'photo'
                      ? global?.color?.orange
                      : type === 'video'
                      ? global?.color?.blue
                      : type === 'location'
                      ? global?.color?.black_30
                      : type === 'numberguesser' || type === 'number_guesser'
                      ? global?.color?.yellow
                      : type === 'multiple_choice'
                      ? global?.color?.teal
                      : type === 'true_false'
                      ? global?.color?.gray3
                      : type === 'fill_in_the_blank'
                      ? global?.color?.gray2
                      : global?.color?.teal
                  }
                  label={`${completeTime ? groupPointsEarned : points} pts`}
                  completed={!!completeTime}
                  completedIcon={groupPointsEarned > 0 ? 'check' : 'close'}
                  completedIconColor={
                    groupPointsEarned > 0
                      ? global?.color?.brightgreen
                      : global?.color?.red
                  }
                  handlePress={() => {
                    handleSelectChallenge(challenge);
                  }}
                />
              );
            })}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default LocationChallengeListModal;
